import { FC, useState } from 'react';
import { useAppSelector } from 'app/store';
import {
  getDisabledAudios,
  getReleaseVersion,
  getVersionSyncStatus,
} from 'app/store/Releases/selectors';
import { useResource } from 'app/utils/hooks';
import {
  fetchLanguageCodesDDLEffect,
  fetchSongDDLEffect,
} from 'app/store/SongList/effects';
import {
  getLanguageCodesSelector,
  getSongDDLSelector,
} from 'app/store/SongList/selectors';
import { TuneCoreVersionFormLoader } from 'app/components/Releases/TuneCoreVersionFormLoader';
import { ReleaseType } from 'types/releases';
import {
  useFetchAudioDependencies,
  useFetchPrimaryAudio,
  useReplaceAudioVersion,
  useUpdateAudioDetails,
  useUpdateVersion,
  useValidations,
} from '../hooks';
import { TuneCoreVersionDetailsFormComponent } from './TuneCoreVersionDetailsFormComponent';
import { AudioData, getPreferredAudioSelectionOption } from './utils';

export const TuneCoreVersionDetailsForm: FC = () => {
  const releaseVersion = useAppSelector(getReleaseVersion);
  const disabledAudios = useAppSelector(getDisabledAudios);
  const [audioData, setAudioData] = useState<AudioData>({ items: [] });
  const [songDDL, songDDLLoading] = useResource(
    fetchSongDDLEffect,
    getSongDDLSelector
  );
  const { audioDataLoading } = useFetchAudioDependencies(setAudioData, {
    validationContext: ReleaseType.TUNECORE,
  });
  const {
    primaryAudioFetching,
    setPrimaryAudioFetching,
    primaryAudio,
    setPrimaryAudio,
  } = useFetchPrimaryAudio();

  const { updateAudioDetails } = useUpdateAudioDetails(
    setPrimaryAudio,
    primaryAudio
  );

  const { replaceAudioVersion, isAudioReplacing } = useReplaceAudioVersion(
    setPrimaryAudioFetching
  );

  const {
    validationsLoading,
    versionValidations,
    versionSongValidations,
    updateVersionAudioValidations,
    fetchAudioDetailsValidations,
  } = useValidations();

  const [languageCodes, languageCodesLoading] = useResource(
    fetchLanguageCodesDDLEffect,
    getLanguageCodesSelector
  );

  const isSyncing =
    useAppSelector((state) =>
      getVersionSyncStatus(state, releaseVersion?.song.versionGuid || '')
    ) || false;

  const { updateVersion } = useUpdateVersion(ReleaseType.TUNECORE);

  if (
    audioDataLoading ||
    validationsLoading ||
    songDDLLoading ||
    !releaseVersion ||
    primaryAudioFetching ||
    languageCodesLoading ||
    isSyncing
  ) {
    return <TuneCoreVersionFormLoader />;
  }

  return releaseVersion &&
    versionValidations &&
    versionSongValidations &&
    languageCodes ? (
    <TuneCoreVersionDetailsFormComponent
      genresDDL={songDDL?.genres || {}}
      releaseVersion={releaseVersion}
      versionSongValidations={versionSongValidations}
      replaceAudioVersion={replaceAudioVersion}
      versionValidations={versionValidations}
      audioData={audioData}
      disabledAudios={disabledAudios}
      updateVersion={updateVersion}
      isAudioReplacing={isAudioReplacing}
      fetchAudioDetailsValidations={fetchAudioDetailsValidations}
      updateAudioValidations={updateVersionAudioValidations}
      updateAudioDetails={updateAudioDetails}
      primaryAudio={getPreferredAudioSelectionOption(primaryAudio)}
      primaryAudioFetching={primaryAudioFetching}
      languageCodes={languageCodes}
      isSyncing={isSyncing}
    />
  ) : null;
};
